import type { FeatureCollection, Geometry } from 'geojson';
import type { CroppingInfo } from '@/components/ImageField.vue';
import type { FileInfo } from '@/services/types/file.types';
import type { BlockParentType, ModuleTypeEnum } from '@/types/modules';
import type { Constants } from '@/constants';

export interface Orderable {
  order_num: number;
}

export interface UserDto {
  id: number;
  email: string;
  language_code: Locales;
  first_name?: string; // Optional because not marked as allowNull: false
  last_name?: string; // Optional because not marked as allowNull: false
  year_birth?: number; // Assuming SMALLINT maps to number, optional
  gender?: number; // Assuming TINYINT maps to number, optional
  postal_code?: string; // Optional because not marked as allowNull: false
  isSuperadmin?: boolean;
  role: number;
}

export interface FileDto {
  url: string;
  color?: string;
  altText?: LocalizableString;
}

export interface CommunityDto extends LanguageEnabled {
  id: number;
  slug: string;
  status: number;
  // Sent once, at creation or duplication: reads never carry it (COCO-3240).
  account_type?: number;
  logo_file: FileDto;
  banner_file: FileDto;
  contact_info: ContactInfo | null;
  custom_url: string | null;
  favicon_file: FileDto | null;
  title: LocalizableString;
  description: LocalizableString;
  report_summary: string | null;
  community_parent: string | null;
  created_at: string;
  updated_at: string;
  options: CommunityOptions;
  admins: AdminPageDto[];
  customPages: CustomPageDto[];
  organization_name: string | null;
  active_consultations_count: number;
  summary: any | null;
  open_graph_file: FileDto | null;
  horizontal_logo_file: FileDto | null;
  default_language: Locales;
  footer_description: LocalizableString | null;
  default_project_theme_key: string;
  default_page_theme_key: string;
}

export type CommunityBrandingMode = 'default' | 'custom';
export type CommunityBrandingFontSource = 'builtin' | 'remote-css';

export interface CommunityBrandingFontConfig {
  source: CommunityBrandingFontSource;
  family: string;
  builtin_key?: string | null;
  css_url?: string | null;
  fallback?: string | null;
}

export interface CommunityBrandingColorTokens {
  bg?: string | null;
  surface?: string | null;
  surface_raised?: string | null;
  surface_hover?: string | null;
  surface_active?: string | null;
  text?: string | null;
  muted?: string | null;
  text_subtle?: string | null;
  disabled?: string | null;
  brand?: string | null;
  brand_contrast?: string | null;
  link?: string | null;
  link_hover?: string | null;
  border?: string | null;
  border_strong?: string | null;
  focus_ring?: string | null;
  success?: string | null;
  success_bg?: string | null;
  warning?: string | null;
  warning_bg?: string | null;
  danger?: string | null;
  danger_bg?: string | null;
  info?: string | null;
  info_bg?: string | null;
}

export interface CommunityBrandingTypographyTokens {
  font_size_display?: string | null;
  font_size_headline?: string | null;
  font_size_title?: string | null;
  font_size_body?: string | null;
  font_size_small?: string | null;
  leading_display?: string | null;
  leading_headline?: string | null;
  leading_body?: string | null;
  font_size_body_mobile?: string | null;
  leading_body_mobile?: string | null;
  tracking_display?: string | null;
  tracking_headline?: string | null;
}

export interface CommunityBrandingTokens {
  colors?: CommunityBrandingColorTokens | null;
  typography?: CommunityBrandingTypographyTokens | null;
}

export interface CommunityBranding {
  mode?: CommunityBrandingMode;
  preset_key?: string | null;
  tokens?: CommunityBrandingTokens | null;
  fonts?: {
    heading?: CommunityBrandingFontConfig | null;
    body?: CommunityBrandingFontConfig | null;
  } | null;
}

export interface CommunityOptions {
  google_tag_manager: {
    enabled: boolean;
    container_id: string | null;
  };
  export: {
    pdf: boolean;
    ai_analysis: boolean;
  };
  consultation: {
    citizen_proposal: boolean;
    map: boolean;
    moderation_comment: boolean;
    guided_consultation: boolean;
  };
  social_networks: {
    facebook: {
      enabled: boolean;
      url: string;
    };
    instagram: {
      enabled: boolean;
      url: string;
    };
    linkedin: {
      enabled: boolean;
      url: string;
    };
    x: {
      enabled: boolean;
      url: string;
    };
  };
  featured_project_id: number | null;
  allow_indexing: boolean;
  loi96: boolean;
  use_demographics: boolean;
  pages: { homepage_id: number | null };
  branding?: CommunityBranding | null;
}

export interface ConsultationOption {
  citizen_proposal: boolean;
  map: boolean;
  anonymous_comments: boolean;
  mailingList: boolean;
  show_private_everyone: boolean;
  automatic_publish: boolean;
  guided_consultation: GuidedConsultationOptions;
}

export type ParticipationMode = number;

export type GuidedConsultationResultsMode = number;

export interface VisibilityCondition {
  id: string;
  sourceFormId: number;
  operator: 'equals';
  valueIds?: number[];
}

export interface VisibilityRuleSet {
  id: string;
  targetType: 'form';
  targetId: number;
  match: 'all';
  conditions: VisibilityCondition[];
}

export interface GuidedConsultationVisibility {
  ruleSets: VisibilityRuleSet[];
}

export interface GuidedConsultationOptions {
  resultsMode: GuidedConsultationResultsMode;
  visibility: GuidedConsultationVisibility;
}

export interface SectionOption {
  citizen_proposal: boolean;
  map: boolean;
  hide_results_comments: boolean;
}

export interface FileDetailsDto {
  id: number;
  bucket: number;
  path: string;
  created_at: string;
  updated_at: string;
}

export interface ConsultationDto extends Orderable, LanguageEnabled {
  id?: number;
  community_id: number;
  slug: string;
  status: number; // As TINYINT is generally used for small integers
  banner_file: FileDto | null;
  title: LocalizableString;
  description: LocalizableString;
  begin_date?: string | null; // Optional and can be null
  end_date?: string | null; // Optional and can be null
  report_summary?: LocalizableString | null; // JSON type in Sequelize maps to any in TypeScript, optional
  type: number; // As TINYINT is generally used for small integers
  welcome_video?: LocalizableString | null;
  welcome_video_type?: string | null; // 'Youtube' | 'Vimeo'
  participation_mode: ParticipationMode;
  options: ConsultationOption;
  user_permissions: UserPermissionDto[];
  attachmentsOpened: boolean;
  private_email_allowed: PrivateAllowed;
  projects: ConsultationProjectDto[];
  open_graph_file: FileDto | null;
  conclusion_message: ConclusionMessage;
  is_template: number;
  clone_from_id?: number;
}

export interface ConsultationProjectDto {
  id: number;
  title: LocalizableString;
  slug: string;
}

export interface DeleteMyAnswersResult {
  responses: number;
  votes: number;
  comments: number;
  commentActions: number;
  proposals: number;
}

export interface SectionDto extends Orderable {
  id?: number;
  consultation_id: number;
  status: number;
  title: LocalizableString;
  description: LocalizableString;
  banner_file?: FileDto | null;
  begin_date?: string | null;
  end_date?: string | null;
  report_summary: string | null;
  options: SectionOption;
  contextOpened: boolean;
  attachmentsOpened: boolean;
}

export interface FormDto extends Orderable {
  id: number;
  type: number;
  status: number;
  section_id: number;
  question: QuestionDto;
  answers: AnswersDto;
  localisation_maps: any; // Replace 'any' with the appropriate type if known
  is_mandatory: number;
  user_id: number;
  approved_date: string | null;
  approved_by: any; // Replace 'any' with the appropriate type if known
  are_comments_enabled: boolean;
  moderation_key: any; // Replace 'any' with the appropriate type if known
  order_num: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user?: UserDto;
  formResponses: any[]; // Replace 'any' with the appropriate type if known
  is_citizen_proposal: number;
  approvalAdmin?: UserDto;
  attachmentsOpened: boolean;
  ai_insights?: AIInsights;
  has_ai_insights?: boolean;
  canChoicesBeDeleted?: boolean;
}

export interface AIInsights {
  ideas: Idea[];
  themes: AITheme[];
  clusters: Cluster[];
  language?: Locales;
}

export interface QuestionDto {
  image: FileDto | null;
  title: LocalizableString;
  content: LocalizableString | null;
}

export type Locales = keyof LocalizableString;

export class LocalizableString {
  en?: string | null;
  fr?: string | null;
}

export interface LanguageEnabled {
  language_enabled: Locales[];
}

export interface MapLayerFileMeta {
  url?: string | null;
  mimetype?: string | null;
  size?: number | null;
  name?: string | null;
}

export interface MapLayerConfig {
  id: number;
  type?: string;
  name?: LocalizableString | null;
  file?: MapLayerFileMeta | null;
  bbox?: [number, number, number, number] | null;
  defaultVisibility?: boolean;
}

export interface MapBounds {
  northEast: [number, number];
  southWest: [number, number];
}

export interface MapStepConfig {
  // Stored as the number input holds it, often a string ("3"): read it with Number().
  maxVotes?: number | string;
  mapStyle?: string | null;
  polygon?: FeatureCollection<Geometry> | null;
  bounds?: MapBounds | null;
  zoom?: number | null;
  answerId?: number;
  categories?: MapCategoryDto[];
  layers?: MapLayerConfig[] | null;
}

export interface AnswersDto {
  steps?: StepDto[];
}

export type StepDto = BaseStepDto | DotVotingStepDto;

interface BaseStepDto {
  id?: number;
  type: string;
  choice_type?: number;
  name?: LocalizableString;
  range?: number[];
  choices?: ChoiceDto[];
  levels?: LevelDto[];
  level_choices?: LevelChoiceDto[];
  choice_template_id?: number;
  answerId?: number;
  maxAnswers?: number;
  otherChoice?: boolean;
  otherChoiceLabel?: LocalizableString | null;
  map?: MapStepConfig | null;
}

export interface DotVotingStepDto extends BaseStepDto {
  type: 'dots';
  dot_voting_type: number;
  dot_voting_measurement_type: number;
  total_budget?: number;
  default_cost_per_choice?: number;
  number_of_steps?: number | null;
  dots: DotDto[];
}

export interface DotDto extends Orderable {
  id?: number;
  title: LocalizableString;
  short_text: LocalizableString;
  long_text: LocalizableString;
  image?: Image | null;
  cost?: number | null;
  order_num: number;
}

interface Image {
  url: string | null;
  mimetype?: string;
  size?: number;
  name?: string;
}

export interface ChoiceDto extends Orderable {
  id?: number;
  text: LocalizableString;
  image: Image | null;
  order_num: number;
}

export interface LevelDto extends Orderable {
  id: number;
  answerId: number;
  name: LocalizableString;
  type: string;
  fullStatistic: boolean;
}

export interface LevelChoiceDto {
  value: number;
  label: LocalizableString;
}

export interface Idea {
  answer_id: string;
  identified_ideas: string[];
}

export interface Cluster {
  theme: string;
  ideas: {
    answer_ids: string[];
    representative_idea: string;
  }[];
}

export interface AITheme {
  theme: string;
  ideas: {
    answer_id: string;
    idea: string;
  }[];
}

export interface FormVoteDto {
  id: number;
  form_response_id: number;
  answer_id: number;
  value_id: any;
  value_text: string;
  form_id: number;
  created_at: string;
  summary: CommentSummaryDto[];
}

export interface ContextDto extends Orderable {
  id: number | null;
  parent: number;
  parent_id: number;
  type: number;
  title: LocalizableString;
  content?: LocalizableString | null;
  image?: LocalizableString;
  file?: LocalizableString;
  content_files?: LocalizableString;
}

export interface CustomPageDto {
  id: number;
  community_id: number;
  type: number;
  status: number;
  slug: string;
  title: LocalizableString;
  content: LocalizableString;
}

/** A page as the admin reads it, without its blocks. */
export interface PageDto extends LanguageEnabled {
  id: number;
  // Null for a page with no address of its own, such as a migrated homepage, served at `/`.
  slug: string | null;
  title: LocalizableString;
  status: number;
  // Null for the community's default page theme.
  theme_key: string | null;
  // Null for the theme's default layout.
  layout_key: string | null;
}

export interface AdminPageDto {
  id: number | null;
  User: UserDto | null;
  role: number;
  object_id: number;
  object_type: number;
}

export interface UserCommunitiesDto {
  communities: UserCommunityDto[];
}

export interface UserCommunityDto {
  object_id: number;
  object_type: number;
  object_title: LocalizableString;
  role: number;
  language_enabled: Locales[];
  // Superadmin only (COCO-3425): a customer's community, whether entering it
  // asks for the customer's request, and the end of the access a reference
  // opened in it, or null when none runs.
  is_customer?: boolean;
  customer_access_required?: boolean;
  customer_access_until?: string | null;
}

export interface CommentDto extends BaseComment {
  form_id: number;
  user_id: number;
  toxicity: number;
  moderation_status: number;
  status: number;
  admin_moderation_reason: string | null;
  is_moderated: number;
  summary: CommentSummaryDto[];
  created_at: string;
  deleted_at: string | null;
  form_vote_id: number;
  user?: UserDto;
}

interface BaseComment {
  id: number;
  comment: string;
  updated_at: string;
}

export interface CommentSummaryDto {
  id: number;
  text: string;
}

export interface MapCategoryDto {
  id?: number;
  name: LocalizableString;
  color: string;
  defaultVisibility: boolean;
}

export interface ProjectDto extends Orderable, LanguageEnabled {
  id?: number;
  community_id: number;
  slug: string;
  status: number;
  visibility: number;
  banner_file: FileDto | null;
  title: LocalizableString;
  description: LocalizableString;
  theme_key?: string | null;
  begin_date?: string | null; // Optional and can be null
  end_date?: string | null; // Optional and can be null
  options: ProjectOption;
  private_email_allowed: PrivateAllowed;
  user_permissions: UserPermissionDto[];
  is_template: number;
  open_graph_file: FileDto | null;
  clone_from_id?: number;
}

export interface ProjectOption {
  show_private_everyone: boolean;
  automatic_publish: boolean;
}

export interface ProjectListItemDto extends Orderable {
  id: number;
  status: number;
  visibility: number;
  title: LocalizableString;
}

export interface ConfigurationDto {
  key: string;
  value: any;
}

export interface BrandingPresetConfigurationItem {
  key: string;
  label: LocalizableString;
  tokens: {
    colors: Required<CommunityBrandingColorTokens>;
    typography: Required<CommunityBrandingTypographyTokens>;
  };
  fonts: {
    heading: CommunityBrandingFontConfig;
    body: CommunityBrandingFontConfig;
  };
}

// The kinds of parent a block sits on, each with its own theme selector and offers.
export type ThemeHost = 'project' | 'page';

// One theme of the manifest served by /configurations as `themes`, already resolved by the API (parent, withdrawn
// types, host-restricted variants): what the admin offers on each host the theme covers.
// The API serves `layouts` on pages only; the `project.themes` fallback has neither `layouts` nor `offeredToAll`.
export interface ThemeManifestEntry {
  key: string;
  label: LocalizableString;
  parent: string | null;
  // Every community may pick the theme; a custom one is set in the database.
  offeredToAll?: boolean;
  hosts: Partial<
    Record<ThemeHost, { offers: Partial<Record<ModuleTypeEnum, ThemeOffer>>; layouts?: ThemePageLayout[] }>
  >;
}

// A named layout a theme offers a page, which stores its key in `layout_key`. The theme's default layout is not one:
// a null `layout_key` means it. Not a ThemeLayoutConfig, which is a block variant.
export interface ThemePageLayout {
  key: string;
  label: LocalizableString;
}

// One theme of the `project.themes` configuration row, which the manifest replaces. Read only as the fallback for an
// API that does not serve `themes` yet; it goes with the row, in COCO-3275.
export interface ProjectThemesRowItem {
  key: string;
  label: LocalizableString;
  modules?: Partial<Record<ModuleTypeEnum, { layouts: ThemeLayoutConfig[] }>>;
}

export interface ThemeOffer {
  // `config.layout` is a variant's position in this list, from 1.
  variants: ThemeLayoutConfig[];
}

export interface ThemeLayoutConfig {
  title: LocalizableString;
  subtitle?: LocalizableString;
  cropping: CroppingInfo;
}

/** A block, whatever its parent: a project's or a page's. */
export interface ProjectModuleDto extends Orderable {
  id?: number;
  title: LocalizableString;
  parent_type: BlockParentType;
  parent_id: number;
  /** @deprecated Derived by the API from the parent, and null for a page block. Blocks E removes it: read parent_type and parent_id. */
  project_id: number | null;
  block_type: number;
  config: ProjectModuleConfigDto;
}

export interface ProjectModuleConfigDto {
  highlight: boolean;
  includeInTableOfContents: boolean;
  ctaButtons?: {
    button1: ProjectModuleConfigCTAButtonDto;
    button2: ProjectModuleConfigCTAButtonDto;
  };
}

export interface ProjectModuleTextConfigDTO extends ProjectModuleConfigDto {
  content: LocalizableString;
}

export interface ProjectModuleTextImageConfigDto extends ProjectModuleTextConfigDTO {
  hasImage: boolean;
  image: FileInfo | null;
  imageAlignment: number;
  imageCaption: LocalizableString;
}

export interface ProjectModuleVideoConfigDto extends ProjectModuleTextConfigDTO {
  videoType: string;
  video: LocalizableString;
  videoAlignment: number;
  videoCaption: LocalizableString;
}

export interface ProjectModuleConsultationConfigDto extends ProjectModuleConfigDto {
  consultationId: number;
  useConsultationBanner: boolean;
  image: FileInfo | null;
}

export interface ProjectModuleAttachmentsConfigDto extends ProjectModuleConfigDto {}

export interface ProjectModuleFaqConfigDto extends ProjectModuleConfigDto {}

export interface ProjectModuleSubscriptionConfigDto extends ProjectModuleConfigDto {}

export interface ProjectModuleTimelineConfigDto extends ProjectModuleConfigDto {}

export interface ProjectModuleContactListConfigDto extends ProjectModuleConfigDto {
  description: LocalizableString;
}

export interface ProjectModuleConfigCTAButtonDto {
  enabled: boolean;
  text: LocalizableString;
  linkType: 'url' | 'module';
  url?: LocalizableString;
  moduleId?: number;
}

export interface ProjectModuleHeroConfigDto extends ProjectModuleConfigDto {
  useProjectTitle: boolean;
  useProjectImage?: boolean;
  displayMode: 'image' | 'video';
  image: FileInfo | null;
  videoType: string;
  video: LocalizableString;
  videoCaption: LocalizableString;
  overlay: ProjectModuleHeroOverlayConfigDto;
  ctaButtons: {
    button1: ProjectModuleConfigCTAButtonDto;
    button2: ProjectModuleConfigCTAButtonDto;
  };
  layout?: number;
}

export interface ProjectModuleHeroOverlayConfigDto {
  backgroundColor: string;
  backgroundOpacity: number;
  textColor: string;
  description: LocalizableString;
  alignment: number;
}

export interface ProjectModuleEventConfigDto extends ProjectModuleConfigDto {
  content: LocalizableString;
}

export interface ProjectModuleEventItemConfigDto {
  id?: number;
  project_module_id: number;
  title: LocalizableString;
  description: LocalizableString;
  begin_date: string;
  end_date?: string | null; // Optional and can be null
  start_time?: string;
  end_time?: string;
  location?: LocalizableString;
  config: ProjectModuleEventItemConfigConfigDto;
  status: number;
}

export interface ProjectModuleEventItemConfigConfigDto {
  image: FileInfo | null;
  ctaButtons: {
    button1: ProjectModuleConfigCTAButtonDto;
  };
}

export interface ProjectModuleFeedbackConfigDto extends ProjectModuleConfigDto {
  anonymousFeedback: boolean;
  description: LocalizableString;
  maxLength: number | null;
  requiresModeration: boolean;
  thankYouMessage: LocalizableString;
}

export interface ProjectModuleQuestionAnswerConfigDto extends ProjectModuleConfigDto {
  description: LocalizableString;
  maxLength: number | null;
  thankYouMessage: LocalizableString;
  noAnswersMessage: LocalizableString;
}

export interface ProjectModuleNewsConfigDto extends ProjectModuleConfigDto {
  content: LocalizableString;
}

export interface ProjectModuleDocumentSubmissionConfigDto extends ProjectModuleConfigDto {
  description: LocalizableString;
  submitButtonLabel: LocalizableString;
  thankYouMessage: LocalizableString;
}

/**
 * Map module config. `bounds` and `zoom` are written by the boundary map in `MapDefinition`,
 * `layers` by the upload and delete endpoints — the admin never edits those three by hand, which
 * is why they are nullable: a freshly created module has no framed area yet.
 */
export interface ProjectModuleMapConfigDto extends ProjectModuleConfigDto {
  description: LocalizableString;
  mapStyle: string | null;
  bounds: MapBounds | null;
  zoom: number | null;
  layers: MapLayerConfig[];
  hideAddressSearch: boolean;
}

export interface ProjectModuleNewsItemConfigDto {
  id?: number;
  project_module_id: number;
  title: LocalizableString;
  description: LocalizableString;
  date: string;
  config: ProjectModuleNewsItemConfigConfigDto;
  status: number;
}

export interface ProjectModuleNewsItemConfigConfigDto {
  image: FileInfo | null;
  content: LocalizableString | null;
}

export interface MailingCampaignDto {
  id?: number;
  title: LocalizableString;
  community_id: number;
  mailing_list_id: number;
  subject?: LocalizableString | null;
  htmlBody?: LocalizableString | null;
  status?: number;
}

export interface MailingListDto {
  id?: number;
  title: LocalizableString;
  parent: number;
  parent_id: number;
  status: number;
  member_count: number;
  title_parent: string;
  type: number;
}

export interface ProjectModule {
  enabled: boolean;
  value: number;
}

export interface UserPermissionDto {
  id?: number;
  user_id: number;
  object_type: number;
  object_id: number;
  role: number;
  terms_approval: boolean;
  email_authorization: boolean;
  User: UserDto;
}

export interface PrivateAllowed {
  email: string[];
  domain: string[];
}

export interface FaqDto {
  id?: number;
  project_module_id: number;
  question: LocalizableString;
  answer: LocalizableString;
  order_num: number;
}

export interface TimelineStepDto {
  id?: number;
  project_module_id: number;
  title: LocalizableString;
  content: LocalizableString;
  status: number;
  order_num: number;
}

export interface ContactListMemberDto {
  id?: number;
  project_module_id: number;
  name: LocalizableString;
  role: LocalizableString;
  organization?: LocalizableString;
  description?: LocalizableString;
  email?: string | null;
  phone?: string | null;
  profile_image?: FileInfo | null;
  socials: ContactListMemberSocialsDto;
  order_num: number;
}

export interface ContactListMemberSocialsDto {
  facebook?: string | null;
  linkedIn?: string | null;
  x?: string | null;
  instagram?: string | null;
}

export interface FeedbackEntryDto {
  id: number;
  project_module_id: number;
  user_id: number;
  message: string;
  status: number;
  toxicity: number | null;
  user: UserDto;
}

type DocumentSubmissionStates = typeof Constants.ProjectModule.DocumentSubmission.State;
export type DocumentSubmissionState = DocumentSubmissionStates[keyof DocumentSubmissionStates];
/** The review list's `?filter=`: one state, or a view across states. `onSite` is the project page's list, in its order. */
export type DocumentSubmissionFilter = DocumentSubmissionState | 'toReview' | 'onSite' | 'all';

/** A participant, reviewer or purger by name; `null` once their account is gone. */
export interface DocumentSubmissionUserName {
  first_name?: string | null;
  last_name?: string | null;
}

/** One row of the admin review list. The comment, the review note and the email only come with the detail. */
export interface DocumentSubmissionListItemDto extends Orderable {
  id: number;
  state: DocumentSubmissionState;
  latestVersionNumber: number;
  latestTitle: string;
  updated_at: string;
  user: DocumentSubmissionUserName | null;
}

export interface DocumentSubmissionVersionDto {
  id: number;
  version_number: number;
  created_at: string;
  title: string;
  published_title: string | null;
  /** `null` once the version is purged. */
  file: { url: string; name: string; size: number } | null;
  participant_comment: string | null;
  status: number;
  published_locales: Locales[];
  review_note: string | null;
  reviewed_at: string | null;
  reviewedBy: DocumentSubmissionUserName | null;
  purged_at: string | null;
  purgedBy: DocumentSubmissionUserName | null;
  /** The decisions the API accepts on this version: admin shows its buttons from these and never works them out. */
  canPublish: boolean;
  canRefuse: boolean;
  canPurge: boolean;
}

/** One document as the review dialog shows it, with every version newest first. */
export interface DocumentSubmissionDetailDto {
  id: number;
  state: DocumentSubmissionState;
  canUnpublish: boolean;
  published_version_id: number | null;
  latest_version_id: number | null;
  user: (DocumentSubmissionUserName & { email: string }) | null;
  versions: DocumentSubmissionVersionDto[];
}

/** Publishing a version: at least one language. A blank title shows the participant's own. */
export interface DocumentSubmissionPublishDto {
  publishedLocales: Locales[];
  publishedTitle: string | null;
  note: string | null;
}

/** Refusing a version; the note goes to the participant by email. */
export interface DocumentSubmissionRefuseDto {
  note: string | null;
}

export interface QuestionAnswerDto {
  id?: number;
  project_module_id: number;
  user_id: number;
  question: string;
  answer: string | null;
  status: number;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  user?: UserDto | null;
}

export interface Pagination {
  page: number;
  rowsNumber: number;
  rowsPerPage: number;
}

export interface ConsultationListItemDto extends Orderable {
  id: number;
  status: number;
  type: number;
  title: LocalizableString;
  begin_date?: string | null;
  end_date?: string | null;
  projects: ConsultationProjectDto[];
}

export interface ProjectConsultationListItemDto extends Orderable {
  id: number;
  title: LocalizableString;
}

export interface ActionMenuItem {
  action: string;
  label: string;
  disabled?: boolean | ((row: any) => boolean);
  // A function gives each row its own text, such as the reason an action is disabled on that row.
  tooltip?: string | ((row: any) => string | undefined);
  rolesAllowed?: number[];
  requiresSuperadmin?: boolean;
}

export type MoveDirection = 'up' | 'down';

// Whether a row may move a place up or down, and when it may not, why.
export interface MoveState {
  isBlocked: boolean;
  reason?: string;
}

export interface UserContribution {
  id: number;
  content: string;
  answer_id?: number | string;
}

export interface ConclusionMessage {
  enabled: boolean;
  content: LocalizableString;
}

export interface ContactInfo {
  phone?: string;
  email?: string;
  address?: LocalizableString;
  website?: LocalizableString;
}

export interface SummaryFormDto {
  id: number;
  title: LocalizableString;
  image?: FileDto | null;
  type: number;
  number_of_participants: number;
  participation_rate: number;
  are_comments_enabled: boolean;
}

export interface SummaryFormCommentDto extends BaseComment {
  first_name?: string | null;
  last_name?: string | null;
  number_of_likes: number;
}

export interface SummaryFormCommentPagination {
  data: SummaryFormCommentDto[];
  pagination: Pagination;
}

export interface SummaryFormOpenVoteDto {
  id: number;
  text: string;
  updated_at: string;
}

export interface SummaryFormOpenVotePagination {
  data: SummaryFormOpenVoteDto[];
  pagination: Pagination;
}

export interface SummaryFormOtherChoiceDto {
  id: number;
  text: string;
}

export interface SummaryFormOtherChoicePagination {
  data: SummaryFormOtherChoiceDto[];
  pagination: Pagination;
}

export interface SummaryFormStatChoiceDto {
  id: number | string;
  text: LocalizableString;
  image?: FileDto | null;
  color?: string | null;
  number_of_votes: number;
  percentage: number;
}

export interface SummaryFormStatLevelDto {
  levels: SummaryFormStatLevelItemDto[];
}

export interface SummaryFormStatLevelItemDto {
  id: number;
  text: LocalizableString;
  number_of_votes: number;
  average: number | null;
  level_choices: SummaryFormStatChoiceDto[];
}

export interface SummaryFormStatProposalDto {
  choices: SummaryFormStatChoiceDto[];
}

export interface SummaryFormStatQuestionDto {
  choices: SummaryFormStatChoiceDto[];
}

export interface SummaryFormStatDotVotingDto {
  dot_voting_type: number | null;
  dot_voting_measurement_type: number | null;
  dots: SummaryFormStatDotVotingChoiceDto[];
}

export interface SummaryFormStatDotVotingChoiceDto {
  id: number;
  text: LocalizableString;
  image: FileDto | null;
  number_of_participants: number;
  percentage_of_participants: number;
  cost: number | null;
  totalAllocation: number;
  distribution: SummaryFormStatDotVotingDotDistributionDto;
}

export interface SummaryFormStatDotVotingDotDistributionDto {
  average: number | null;
  median: number | null;
  share_of_total_allocations: number | null;
}

export interface SummaryFormStatDto {
  id: number;
  type: number;
  stats:
    | SummaryFormStatLevelDto
    | SummaryFormStatProposalDto
    | SummaryFormStatQuestionDto
    | SummaryFormStatDotVotingDto
    | null;
}
export interface SummarySectionDto {
  id: number;
  title: LocalizableString;
  number_of_forms: number;
}

export interface SummaryExportingOptions {
  consultationId: number;
  sectionId?: number;
  formId?: number;
}
